<template>
    <div :class="{ 'owner px-4': cardStyle }">
        <div class="owner__photo" v-if="!hideUserInfo">
            <a :href="`/adverts/posted-by/${user.slug}`" class="owner__avatar" :class="{
                'owner__avatar--logo': user.is_company,
            }" title="View all users's adverts">
                <img v-if="user.image_url" :src="user.image_url" :alt="user.public_name" />
                <span v-else class="owner__initials">
                    {{ user.public_name_initials }}
                </span>
            </a>
            <UploadPhoto v-if="hasUploadPhoto" />
        </div>
        <h5 class="text-center" v-if="!hideUserInfo">
            {{ user.public_name }}
        </h5>
        <div class="company-card-address" v-if="user.is_company && adContext && !hideUserInfo">
            <MapPinIcon />
            <p> {{ user.address || 'N/A' }}</p>
        </div>
        <p class="text-center" v-if="!hideUserInfo">
            {{ !user.is_company && user.created_at ? 'joined ' : '' }}
            <span :class="{ 'text-bold': !user.is_company }">
                {{ (user.is_company && user.account_label) || user.created_at }}
            </span>
        </p>

        <template v-if="user.is_individual_landlord && !adContext">
            <button type="button" class="btn btn--secondary w-auto mb-2" @click.prevent="onClickToggleLandlordInfo">
                {{ `${showLandlordInfo ? 'Hide' : 'Show'} info` }}
            </button>
        </template>

        <div class="owner__actions w-100 text-center"
            v-if="adContext || !(user.is_individual_landlord && !showLandlordInfo)">
            <p v-if="(!hideCall && adContext) || !user.is_company">
                <a v-if="primaryPhoneNumberE164 && isPrimaryCard" type="button" id="primaryPhoneNumberE164"
                    class="btn btn--secondary-v2 w-50" :class="{ 'w-100': hideUserInfo }"
                    :disabled="disableCall || isLoadingCall" :href="`tel:${primaryPhoneNumberE164}`"
                    @click="onClickCall" target="_self">
                    <span class="over-tablet-only">{{ primaryPhoneNumberE164 }}</span>
                    <span class="less-tablet-only">Call</span>
                </a>
                <a v-else-if="secondaryPhoneNumberE164 && !isPrimaryCard" type="button"
                    class="btn btn--secondary-v2 w-50" :class="{ 'w-100': hideUserInfo }"
                    :disabled="disableCall || isLoadingCall" :href="`tel:${secondaryPhoneNumberE164}`"
                    @click="onClickCall" target="_self">
                    <span class="over-tablet-only">{{ secondaryPhoneNumberE164 }}</span>
                    <span class="less-tablet-only">Call</span>
                </a>
                <button v-else type="button" class="btn btn--secondary-v2 w-50" :class="{ 'w-100': hideUserInfo }"
                    :disabled="disableCall || isLoadingCall" @click.prevent="showPhoneNumber">
                    {{ adContext ? 'Call' : 'Show number' }}
                </button>
            </p>
            <p v-if="!hideChat">
                <button v-if="conversationSid" type="button" class="btn btn--secondary-v2 w-50"
                    :class="{ 'w-100': hideUserInfo }" @click.prevent="redirectTo(`/chat/${conversationSid}`)">
                    Open Chat
                </button>
                <button v-else type="button" class="btn btn--secondary-v2 w-50" :class="{ 'w-100': hideUserInfo }"
                    :disabled="disableChat" @click.prevent="onClickChat">
                    Chat
                </button>
            </p>
        </div>

        <div class="owner__info w-100 text-center" v-if="!hideUserInfo && !adContext">
            <template v-if="user.is_company">
                <div class="company-card-phone mb-2">
                    <i class="ri-phone-line" />
                    {{ user.phone_number_e164 || user.public_phone_number_e164 || 'N/A' }}
                </div>
                <div class="company-card-address">
                    <MapPinIcon class="primary-text-color" />
                    <p> {{ user.address || 'N/A' }}</p>
                </div>
                <div class="company-card-contact">
                    <MailIcon />
                    <a href="#" @click.prevent="showContactUserModal = true">Contact</a>
                </div>

                <AdMap v-if="user.marker" :markers="[this.user.marker]" class="mt-3 w-100 company-card-map" />

            </template>
            <template v-else>
                <div class="renter-card-description" v-if="showLandlordInfo">
                    <div class="renter-card-description-item">
                        <span> Deposit scheme: </span><br />
                        <span class="secondary-text-color">{{ user.deposit_scheme_formatted || 'N/A' }}</span>
                    </div>
                    <div class="renter-card-description-item">
                        <span> Acredited landlord body: </span><br />
                        <span class="secondary-text-color">{{ user.acredited_landlord_body_formatted || 'N/A' }}</span>
                    </div>
                </div>
            </template>
            <div v-show="user.is_company || (user.is_individual_landlord && showLandlordInfo)"
                class="user-card-about mt-4">
                <h6 class="mb-1">About {{ user.is_individual_landlord ? 'me' : 'us' }}:</h6>
                <p :style="descriptionStyle" ref="descriptionP">{{ user.description || 'No description' }}</p>
            </div>
            <div class="see-more-btn" v-if="descriptionHeight >= 140"
                v-show="user.is_company || (user.is_individual_landlord && showLandlordInfo)">
                <button class="primary-color-text" @click.prevent="expanded = true" v-if="!expanded">See more</button>
                <button class="primary-color-text" @click.prevent="expanded = false" v-else>See less</button>
            </div>
        </div>

        <ContactUserModal :targetUser="user" :show-modal="showContactUserModal" @close="showContactUserModal = false" />
    </div>
</template>

<script>
import AdMap from '@/components/AdDetails/partials/AdMap.vue';
import UploadPhoto from '@components/UserProfile/partials/UploadPhoto.vue';
import { redirectTo } from '@helpers/url-handler';
import resizeMixin from '@mixins/resizeMixin';
import { getGoogleMapsAPI } from 'gmap-vue';
import { mapActions, mapState } from 'vuex';
import ContactUserModal from './ContactUserModal.vue';

const MailIcon = () => import('@components/partials/icons/MailIcon.vue');
const MapPinIcon = () => import('@components/partials/icons/MapPinIcon.vue');

export default {
    mixins: [resizeMixin],
    components: {
        UploadPhoto,
        MapPinIcon,
        MailIcon,
        AdMap,
        ContactUserModal,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        disableCall: {
            type: Boolean,
            default: false,
        },
        hideCall: {
            type: Boolean,
            default: false,
        },
        disableChat: {
            type: Boolean,
            default: false,
        },
        hideChat: {
            type: Boolean,
            default: false,
        },
        hideUserInfo: {
            type: Boolean,
            default: false,
        },
        conversationSid: {
            type: String,
        },
        hasUploadPhoto: {
            type: Boolean,
            default: false,
        },
        isPrimaryCard: {
            type: Boolean,
            default: true,
        },
        adContext: {
            type: Object,
            required: false,
        },
        cardStyle: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isLoadingCall: false,
            expanded: false,
            showLandlordInfo: false,
            descriptionHeight: null,
            retries: 0,
            showContactUserModal: false,
        };
    },
    computed: {
        ...mapState('adDetails', ['primaryPhoneNumberE164', 'secondaryPhoneNumberE164']),
        descriptionStyle() {
            return {
                lineClamp: this.expanded ? 'initial' : 8,
            };
        },
        google: getGoogleMapsAPI,
    },
    watch: {
        showLandlordInfo() {
            this.updateDescriptionHeight();
        },
    },
    mounted() {
        if (this.user.is_company || (this.user.is_individual_landlord && this.showLandlordInfo)) {
            this.updateDescriptionHeight();
        }
        if (this.adContext) {
            this.showLandlordInfo = true;
        }
    },
    methods: {
        redirectTo,
        ...mapActions('adDetails', ['getPhoneNumber', 'callPhoneNumber']),
        showPhoneNumber() {
            this.isLoadingCall = true;

            this.getPhoneNumber({
                userUuid: this.user.uuid,
                isPrimaryPhoneNumber: this.isPrimaryCard,
                adUuid: this.adContext ? this.adContext.uuid : null,
            })
                .then(() => {
                    if (this.primaryPhoneNumberE164 && this.isMobile) {
                        document.getElementById('primaryPhoneNumberE164').click();
                    }
                })
                .finally(() => {
                    this.isLoadingCall = false;
                });
        },
        onClickCall() {
            this.callPhoneNumber({
                userUuid: this.user.uuid,
                adUuid: this.adContext ? this.adContext.uuid : null,
            });
        },
        onClickChat() {
            if (!this.$store.state.user.user) {
                this.$root.$emit('open-login-modal');
                return;
            }
            this.$emit('handle-chat');
        },
        onClickToggleLandlordInfo() {
            if (!this.$store.state.user.user) {
                this.$root.$emit('open-login-modal');
                return;
            }
            this.showLandlordInfo = !this.showLandlordInfo;
        },
        updateDescriptionHeight() {
            if (this.user.is_individual_landlord && !this.showLandlordInfo) return;

            if (!this.descriptionHeight && this.$refs && this.$refs.descriptionP) {
                this.expanded = true;
                this.$nextTick(() => {
                    this.descriptionHeight = this.$refs.descriptionP.clientHeight || 0;
                    this.expanded = false;
                });
            } else if (this.retries < 10) {
                this.retries++;
                setTimeout(() => {
                    this.updateDescriptionHeight();
                }, 100);
            }
        },
    },
};
</script>
